/**
 * ERPDashboardPage
 * 
 * Dashboard Gerencial - Consolidação de Dados Operacionais
 * Exibe visão executiva com KPIs, gráficos e métricas operacionais.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  TrendingUp, DollarSign, Package, 
  ShoppingCart, Users, Calendar, Clock,
  ChevronRight,
  Star, Crown,
  Zap, BrainCircuit, ArrowRight, ArrowUpRight, ListChecks,
  PieChart, Target, CalendarDays, Receipt, RotateCcw, Percent
} from 'lucide-react'
import { erpDashboardService } from '../../../services/erpDashboardService'
import Avatar from '../../../components/Avatar/Avatar'
import CashFlow48hSummary from '../../../components/ERP/FinanceDashboard/CashFlow48hSummary/CashFlow48hSummary'
import MonthlyRevenueBarChart from '../../../components/ERP/Dashboard/MonthlyRevenueBarChart/MonthlyRevenueBarChart'
import ProductRankingPanel from '../../../components/ERP/Dashboard/ProductRankingPanel/ProductRankingPanel'
import StockoutAlertWidget from '../../../components/ERP/Purchases/StockoutAlertWidget/StockoutAlertWidget'
import { CalendarRangePicker } from '../../../components/ERP/Calendar/Calendar'
import { formatCurrencyBRL } from '../../../utils/formatCurrency'
import { formatDateOnly, parseDateOnly, toDateInputValue } from '../../../utils/dateUtils'
import './ERPDashboardPage.css'

function getDefaultMonthRange() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    from: toDateInputValue(start),
    to: toDateInputValue(end)
  }
}

function buildPeriodLabel(from, to) {
  if (!from || !to) return 'Selecionar período'
  const start = parseDateOnly(from)
  const end = parseDateOnly(to)
  const lastDayOfMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
  const isFullMonth =
    start.getDate() === 1 &&
    end.getDate() === lastDayOfMonth &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  if (isFullMonth) {
    const label = start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    return label.charAt(0).toUpperCase() + label.slice(1)
  }
  return `${formatDateOnly(from)} — ${formatDateOnly(to)}`
}

function ERPDashboardPage() {
  const navigate = useNavigate()
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const hasDisplayedDataRef = useRef(false)
  const periodBtnRef = useRef(null)
  const [error, setError] = useState(null)
  const [hoveredCategory, setHoveredCategory] = useState(null)
  const [hoveredSalesPoint, setHoveredSalesPoint] = useState(null)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [dateRange, setDateRange] = useState(getDefaultMonthRange)
  const [salesSummaryType, setSalesSummaryType] = useState('total')
  const [isSummaryDropdownOpen, setIsSummaryDropdownOpen] = useState(false)

  const periodLabel = useMemo(
    () => buildPeriodLabel(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  )

  const periodYear = useMemo(() => {
    const [year] = (dateRange.from || '').split('-').map(Number)
    return year || new Date().getFullYear()
  }, [dateRange.from])

  // Carregar dados do dashboard
  const loadDashboardData = useCallback(async () => {
    if (hasDisplayedDataRef.current) {
      setRefreshing(true)
    } else {
      setInitialLoading(true)
    }
    setError(null)
    try {
      const response = await erpDashboardService.getDashboardData({
        period: 'custom',
        startDate: dateRange.from,
        endDate: dateRange.to,
        year: periodYear
      })
      
      if (response.success) {
        setDashboardData(response.data)
        hasDisplayedDataRef.current = true
      } else {
        setError('Erro ao carregar dados do dashboard')
      }
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setInitialLoading(false)
      setRefreshing(false)
    }
  }, [dateRange.from, dateRange.to, periodYear])

  useEffect(() => {
    loadDashboardData()
  }, [loadDashboardData])

  const handlePeriodRangeChange = (from, to) => {
    if (!from || !to) return
    setDateRange({ from, to })
  }

  const handleClearPeriodFilter = () => {
    setDateRange(getDefaultMonthRange())
  }

  const formatNumber = (val) => {
    if (!val && val !== 0) return '0'
    if (val >= 1000) {
      return `${(val / 1000).toFixed(1)}k`
    }
    return val.toString()
  }

  const formatPercentChange = (value) => {
    if (value === 0) return '0%'
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  // Preparar KPIs
  const stats = useMemo(() => {
    if (!dashboardData?.strategicKPIs) return []
    
    const kpis = dashboardData.strategicKPIs
    const returnsHint = (kpis.revenue?.returnsAmount || 0) > 0
      ? `Líquido · devoluções ${formatCurrencyBRL(kpis.revenue.returnsAmount)}`
      : null
    return [
      { 
        label: 'Faturamento', 
        subtitle: returnsHint || periodLabel,
        value: formatCurrencyBRL(kpis.revenue?.value || 0), 
        change: formatPercentChange(kpis.revenue?.change || 0), 
        up: kpis.revenue?.changeType === 'increase', 
        icon: DollarSign, 
        color: 'text-emerald-500', 
        bg: 'bg-emerald-500/10' 
      },
      { 
        label: 'Venda Diária', 
        subtitle: 'Hoje',
        value: formatCurrencyBRL(kpis.dailySales?.value || 0), 
        change: formatPercentChange(kpis.dailySales?.change || 0), 
        up: kpis.dailySales?.changeType === 'increase', 
        icon: CalendarDays, 
        color: 'text-violet-500', 
        bg: 'bg-violet-500/10' 
      },
      { 
        label: 'Descontos', 
        subtitle: periodLabel,
        value: formatCurrencyBRL(kpis.discountGiven?.value || 0), 
        change: formatPercentChange(kpis.discountGiven?.change || 0), 
        up: kpis.discountGiven?.changeType === 'decrease', 
        icon: Percent, 
        color: 'text-orange-500', 
        bg: 'bg-orange-500/10' 
      },
      { 
        label: 'Ticket Médio', 
        subtitle: periodLabel,
        value: formatCurrencyBRL(kpis.averageTicket?.value || 0), 
        change: kpis.dailySales?.orderCount
          ? `Hoje ${formatCurrencyBRL(kpis.averageTicket?.dailyValue || 0)}`
          : '—',
        up: true, 
        icon: Receipt, 
        color: 'text-cyan-500', 
        bg: 'bg-cyan-500/10' 
      },
      { 
        label: 'Pedidos', 
        subtitle: periodLabel,
        value: formatNumber(kpis.orders?.value || 0), 
        change: `${kpis.orders?.value || 0} no período`, 
        up: true, 
        icon: ShoppingCart, 
        color: 'text-primary-500', 
        bg: 'bg-primary-500/10' 
      },
      { 
        label: 'Estoque Crítico', 
        subtitle: 'Abaixo do mínimo',
        value: formatNumber(kpis.stock?.value || 0), 
        change: `${kpis.stock?.value || 0} itens`, 
        up: false, 
        icon: Package, 
        color: 'text-amber-500', 
        bg: 'bg-amber-500/10' 
      },
      { 
        label: 'Leads', 
        subtitle: periodLabel,
        value: formatNumber(kpis.leads?.value || 0), 
        change: (kpis.leads?.value || 0) > 0 ? 'Ativos' : 'Sem novos', 
        up: true, 
        icon: Users, 
        color: 'text-blue-500', 
        bg: 'bg-blue-500/10' 
      },
    ]
  }, [dashboardData, periodLabel])

  // Preparar gráfico de pizza (OPEX)
  const donutChart = useMemo(() => {
    if (!dashboardData?.opexDistribution?.categories) return []
    
    const categories = dashboardData.opexDistribution.categories
    let currentOffset = 0
    const radius = 70
    const circumference = 2 * Math.PI * radius

    return categories.map((cat) => {
      const strokeDasharray = `${(cat.percentage * circumference) / 100} ${circumference}`
      const strokeDashoffset = -currentOffset
      currentOffset += (cat.percentage * circumference) / 100
      return { ...cat, strokeDasharray, strokeDashoffset }
    })
  }, [dashboardData])

  // Dias reais da API (métricas e totais)
  const salesHistoryRaw = useMemo(() => {
    if (!dashboardData?.dailySalesHistory?.days) return []
    return dashboardData.dailySalesHistory.days
  }, [dashboardData])

  const salesHistory = salesHistoryRaw

  const salesChartMetrics = useMemo(() => {
    const dataDays = salesHistoryRaw
    if (!dataDays.length) return { total: 0, average: 0, max: 0, min: 0, minNonZero: 0 }
    const values = dataDays.map(d => d.totalAmount || 0)
    const nonZeroValues = values.filter(v => v > 0)
    const total = values.reduce((a, b) => a + b, 0)
    const average = total / dataDays.length
    const max = Math.max(...values)
    const min = Math.min(...values)
    const minNonZero = nonZeroValues.length > 0 ? Math.min(...nonZeroValues) : 0
    return { total, average, max, min, minNonZero }
  }, [salesHistoryRaw])

  const maxValForChart = useMemo(() => {
    if (!salesChartMetrics.max) return 10000
    
    // Se há uma grande diferença entre max e min não-zero, usar uma escala adaptativa
    // que garante que valores pequenos sejam visíveis
    const { max, minNonZero } = salesChartMetrics
    
    // Se a diferença é muito grande (mais de 100x), usar escala que considera ambos
    if (minNonZero > 0 && max / minNonZero > 100) {
      // Usar uma escala que garante que o menor valor não-zero tenha pelo menos 5% da altura
      // e o maior valor use cerca de 95% da altura
      const minHeightPercent = 0.05 // 5% da altura para o menor valor não-zero
      const maxHeightPercent = 0.95 // 95% da altura para o maior valor
      
      // Calcular o maxValForChart baseado na proporção desejada
      // Se minNonZero deve ter 5% de altura: minNonZero / maxValForChart * 160 = 200 * minHeightPercent
      // maxValForChart = minNonZero / (200 * minHeightPercent / 160)
      // Mas também precisamos garantir que max tenha 95%: max / maxValForChart * 160 = 200 * maxHeightPercent
      // maxValForChart = max / (200 * maxHeightPercent / 160)
      
      // Usar o maior dos dois cálculos para garantir ambos
      const maxValForMin = minNonZero / (minHeightPercent * 200 / 160)
      const maxValForMax = max / (maxHeightPercent * 200 / 160)
      
      return Math.max(maxValForMin, maxValForMax) * 1.1 // Adicionar 10% de margem
    }
    
    // Caso padrão: usar 1.2x do máximo
    return salesChartMetrics.max * 1.2
  }, [salesChartMetrics])

  const salesPath = useMemo(() => {
    if (salesHistory.length === 0) return ''

    const xDivisor = Math.max(salesHistory.length - 1, 1)
    
    const points = salesHistory.map((d, i) => {
      const x = (i / xDivisor) * 800
      const value = d.totalAmount || 0
      
      // Calcular Y normalmente
      let y
      if (value === 0) {
        y = 200 // Valores zero ficam na base
      } else {
        const calculatedY = 200 - (value / maxValForChart) * 160
        
        // Aplicar altura mínima apenas em casos extremos:
        // quando há grande diferença entre valores (100x+) E o valor calculado está muito próximo da base
        const { max, minNonZero } = salesChartMetrics
        const hasLargeDifference = minNonZero > 0 && max / minNonZero > 100
        const minHeight = 4 // altura mínima em pixels (2% de 200)
        const threshold = 196 // 4px acima da base
        
        // Só aplicar altura mínima se:
        // 1. Há grande diferença entre valores (caso extremo)
        // 2. E o valor calculado está muito próximo da base (indicando valor muito pequeno)
        if (hasLargeDifference && calculatedY > threshold) {
          y = 200 - minHeight
        } else {
          // Para valores normais (maioria dos casos), usar cálculo direto
          y = calculatedY
        }
      }
      
      return { x, y }
    })

    if (points.length === 0) return ''
    let path = `M ${points[0].x},${points[0].y}`
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i]
      const p1 = points[i + 1]
      const cp1x = p0.x + (p1.x - p0.x) / 2
      path += ` C ${cp1x},${p0.y} ${cp1x},${p1.y} ${p1.x},${p1.y}`
    }
    return path
  }, [salesHistory, maxValForChart, salesChartMetrics])

  const handleProductClick = (product) => {
    if (product?.id) {
      navigate(`/erp/produtos/produtos/${product.id}`)
    }
  }

  if (initialLoading && !dashboardData) {
    return (
      <div className="erp-dashboard-loading">
        <p>Carregando dados do dashboard...</p>
      </div>
    )
  }

  if (error && !dashboardData) {
    return (
      <div className="erp-dashboard-error">
        <p>{error}</p>
        <button type="button" onClick={loadDashboardData}>Tentar novamente</button>
      </div>
    )
  }

  if (!dashboardData) {
    return null
  }

  const isBusy = initialLoading || refreshing

  return (
    <div className={`erp-dashboard${refreshing ? ' is-refreshing' : ''}`}>
      {/* Page Header */}
      <div className="erp-dashboard-header">
        <div>
          <h1 className="erp-dashboard-title">
            Análise Gerencial
            {refreshing && (
              <RotateCcw
                size={20}
                className="erp-dashboard-refresh-icon"
                aria-hidden
              />
            )}
          </h1>
          <p className="erp-dashboard-subtitle">Consolidação de Dados Operacionais</p>
        </div>
        <div className="erp-dashboard-header-actions">
          <button
            type="button"
            onClick={loadDashboardData}
            className="erp-dashboard-reload-btn"
            disabled={isBusy}
            aria-label="Atualizar dados do dashboard"
            title="Atualizar dados"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'erp-dashboard-reload-icon is-spinning' : 'erp-dashboard-reload-icon'}
            />
          </button>
          <button 
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className="erp-dashboard-period-btn erp-dashboard-period-btn--interactive"
            title="Filtrar período do dashboard"
          >
            <Calendar size={14} className="erp-dashboard-period-icon" /> 
            <span>{periodLabel}</span>
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={dateRange.from}
            endValue={dateRange.to}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Período do dashboard"
            subtitle="KPIs, vendas e rankings"
          />
        </div>
      </div>

      {error && (
        <div className="erp-dashboard-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadDashboardData}>Tentar novamente</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="erp-dashboard-kpi-grid">
        {stats.map((stat, idx) => (
          <div key={idx} className="erp-dashboard-kpi-card">
            <div className="erp-dashboard-kpi-header">
              <div className={`erp-dashboard-kpi-icon ${stat.bg} ${stat.color}`}>
                <stat.icon size={20} />
              </div>
              <span className={`erp-dashboard-kpi-change ${stat.up ? 'positive' : 'negative'}`}>
                {stat.change}
              </span>
            </div>
            <p className="erp-dashboard-kpi-label">{stat.label}</p>
            {stat.subtitle && (
              <p className="erp-dashboard-kpi-sublabel">{stat.subtitle}</p>
            )}
            <h3 className="erp-dashboard-kpi-value">{stat.value}</h3>
          </div>
        ))}
      </div>

      {/* Faturamento mensal do ano */}
      <MonthlyRevenueBarChart
        data={dashboardData.monthlyRevenueYear}
        formatValue={formatCurrencyBRL}
      />

      {/* Gráfico de Vendas Diárias */}
      <div className="erp-dashboard-sales-chart">
        <div className="erp-dashboard-sales-chart-header">
          <div>
            <h3 className="erp-dashboard-sales-chart-title">
              <TrendingUp size={16} className="erp-dashboard-sales-chart-title-icon" /> 
              Desempenho de Vendas Diárias
            </h3>
            <p className="erp-dashboard-sales-chart-subtitle">
              Histórico de faturamento — {periodLabel}
            </p>
          </div>
          
          <div className="erp-dashboard-sales-chart-actions">
            <div className="erp-dashboard-sales-chart-summary">
              <p className="erp-dashboard-sales-chart-summary-label">
                {salesSummaryType === 'total' ? 'Total Acumulado' : 
                 salesSummaryType === 'average' ? 'Média Diária' : 'Pico de Vendas'}
              </p>
              <p className="erp-dashboard-sales-chart-summary-value">
                {formatCurrencyBRL(salesChartMetrics[salesSummaryType] || 0)}
              </p>
            </div>
            
            <div className="erp-dashboard-sales-chart-dropdown">
              <button 
                onClick={() => setIsSummaryDropdownOpen(!isSummaryDropdownOpen)}
                className="erp-dashboard-sales-chart-dropdown-btn"
              >
                Resumo: {salesSummaryType === 'total' ? 'Total' : 
                         salesSummaryType === 'average' ? 'Média' : 'Máximo'}
                <ChevronRight size={14} className={`erp-dashboard-sales-chart-dropdown-icon ${isSummaryDropdownOpen ? 'open' : ''}`} />
              </button>
              
              {isSummaryDropdownOpen && (
                <div className="erp-dashboard-sales-chart-dropdown-menu">
                  {[
                    { id: 'total', label: 'Total Acumulado' },
                    { id: 'average', label: 'Média Diária' },
                    { id: 'max', label: 'Pico de Vendas' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        setSalesSummaryType(opt.id)
                        setIsSummaryDropdownOpen(false)
                      }}
                      className={`erp-dashboard-sales-chart-dropdown-item ${salesSummaryType === opt.id ? 'active' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="erp-dashboard-sales-chart-content">
          <svg viewBox="0 0 800 200" className="erp-dashboard-sales-chart-svg" preserveAspectRatio="none">
            <defs>
              <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
              </linearGradient>
            </defs>
            
            <path d={`${salesPath} L 800,200 L 0,200 Z`} fill="url(#salesGrad)" />
            <path d={salesPath} fill="none" stroke="#22c55e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            
            {salesHistory.map((d, i) => {
              const value = d.totalAmount || 0
              let cy
              if (value === 0) {
                cy = 200
              } else {
                const calculatedCy = 200 - (value / maxValForChart) * 160
                const { max, minNonZero } = salesChartMetrics
                const hasLargeDifference = minNonZero > 0 && max / minNonZero > 100
                const minHeight = 4
                const threshold = 196

                if (hasLargeDifference && calculatedCy > threshold) {
                  cy = 200 - minHeight
                } else {
                  cy = calculatedCy
                }
              }

              const xDivisor = Math.max(salesHistory.length - 1, 1)
              
              return (
                <circle 
                  key={d.date || i} 
                  cx={(i / xDivisor) * 800} 
                  cy={cy}
                  r="4" 
                  fill="white" 
                  stroke="#22c55e" 
                  strokeWidth="2" 
                  onMouseEnter={() => setHoveredSalesPoint(i)}
                  onMouseLeave={() => setHoveredSalesPoint(null)}
                  className="erp-dashboard-sales-chart-point"
                />
              )
            })}
          </svg>

          {hoveredSalesPoint !== null && salesHistory[hoveredSalesPoint] && (
            <div 
              className="erp-dashboard-sales-chart-tooltip"
              style={{ 
                left: `${(hoveredSalesPoint / (salesHistory.length - 1)) * 100}%`, 
                transform: hoveredSalesPoint > salesHistory.length / 2 ? 'translateX(-110%)' : 'translateX(10%)' 
              }}
            >
              <p className="erp-dashboard-sales-chart-tooltip-label">
                {salesHistory[hoveredSalesPoint].label}
              </p>
              <p className="erp-dashboard-sales-chart-tooltip-value">
                {formatCurrencyBRL(salesHistory[hoveredSalesPoint].totalAmount || 0)}
              </p>
              <div className="erp-dashboard-sales-chart-tooltip-details">
                <div className="erp-dashboard-sales-chart-tooltip-detail">
                  <span>Pedidos</span>
                  <span>{salesHistory[hoveredSalesPoint].orderCount || 0}</span>
                </div>
                <div className="erp-dashboard-sales-chart-tooltip-detail">
                  <span>Ticket Médio</span>
                  <span>
                    {formatCurrencyBRL(
                      (salesHistory[hoveredSalesPoint].totalAmount || 0) / 
                      Math.max(salesHistory[hoveredSalesPoint].orderCount || 1, 1)
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="erp-dashboard-sales-chart-labels">
          {salesHistory
            .filter((_, i, arr) => i % Math.ceil(arr.length / 6) === 0)
            .map((d) => (
              <span key={d.date} className="erp-dashboard-sales-chart-label">{d.label}</span>
            ))}
        </div>
      </div>

      {/* Ranking de consultores + Projeção 48h */}
      <div className="erp-dashboard-sales-followup-grid">
        <div className="erp-dashboard-leaderboard">
          <div className="erp-dashboard-leaderboard-header">
            <h3 className="erp-dashboard-leaderboard-title">
              <Star size={16} className="erp-dashboard-leaderboard-title-icon" /> 
              Ranking de Consultores
            </h3>
            <div className="erp-dashboard-leaderboard-badge">{periodLabel}</div>
          </div>

          <div className="erp-dashboard-leaderboard-grid">
            {(dashboardData.consultantRanking || []).map((consultant, i) => (
              <div key={consultant.id} className="erp-dashboard-leaderboard-card">
                <div className="erp-dashboard-leaderboard-card-header">
                  <div className="erp-dashboard-leaderboard-avatar-wrapper">
                    <Avatar 
                      name={consultant.name} 
                      size="lg" 
                      imageUrl={consultant.avatar}
                    />
                    <div className={`erp-dashboard-leaderboard-rank ${
                      i === 0 ? 'rank-1' : i === 1 ? 'rank-2' : i === 2 ? 'rank-3' : 'rank-other'
                    }`}>
                      {i === 0 ? <Crown size={14} /> : i + 1}
                    </div>
                  </div>
                  <div className="erp-dashboard-leaderboard-info">
                    <p className="erp-dashboard-leaderboard-name">{consultant.name}</p>
                    <p className="erp-dashboard-leaderboard-conversion">Conversão: {consultant.conversion}</p>
                  </div>
                </div>
                <div className="erp-dashboard-leaderboard-card-footer">
                  <p className="erp-dashboard-leaderboard-footer-label">Faturamento Acumulado</p>
                  <p className="erp-dashboard-leaderboard-footer-value">
                    {formatCurrencyBRL(consultant.sales)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <CashFlow48hSummary
          className="erp-dashboard-cashflow-48h"
          variant="dark"
          data={dashboardData?.projection48h}
        />
      </div>

      {/* Rankings de produtos */}
      <div className="erp-dashboard-products-grid">
        <ProductRankingPanel
          variant="sales"
          title="Mais Vendidos"
          subtitle={`Por quantidade · ${periodLabel}`}
          items={dashboardData.productRankings?.topSelling || []}
          formatValue={formatCurrencyBRL}
          onItemClick={handleProductClick}
        />
        <ProductRankingPanel
          variant="margin"
          title="Melhores Margens"
          subtitle={`Lucro bruto no período · ${periodLabel}`}
          items={dashboardData.productRankings?.topMargin || []}
          formatValue={formatCurrencyBRL}
          onItemClick={handleProductClick}
          emptyMessage="Nenhum produto com custo e venda no período"
        />
      </div>

      {/* Camada de Gráficos Executivos */}
      <div className="erp-dashboard-executive-grid">
        {/* Gráfico: Distribuição de OPEX */}
        <div className="erp-dashboard-opex-card">
          <div className="erp-dashboard-opex-header">
            <h3 className="erp-dashboard-opex-title">Distribuição OPEX</h3>
            <PieChart size={16} className="erp-dashboard-opex-icon" />
          </div>

          <div className="erp-dashboard-opex-content">
            <div className="erp-dashboard-opex-donut">
              <svg viewBox="0 0 160 160" className="erp-dashboard-opex-donut-svg">
                {donutChart.map((cat, i) => (
                  <circle
                    key={i}
                    cx="80" cy="80" r="70"
                    fill="transparent"
                    stroke={cat.color}
                    strokeWidth={hoveredCategory === i ? "16" : "12"}
                    strokeDasharray={cat.strokeDasharray}
                    strokeDashoffset={cat.strokeDashoffset}
                    className="erp-dashboard-opex-donut-segment"
                    onMouseEnter={() => setHoveredCategory(i)}
                    onMouseLeave={() => setHoveredCategory(null)}
                  />
                ))}
              </svg>
              <div className="erp-dashboard-opex-donut-center">
                <p className="erp-dashboard-opex-donut-center-label">Total</p>
                <p className="erp-dashboard-opex-donut-center-value">100%</p>
              </div>
            </div>

            <div className="erp-dashboard-opex-legend">
              {donutChart.slice(0, 3).map((cat, i) => (
                <div 
                  key={i} 
                  className={`erp-dashboard-opex-legend-item ${hoveredCategory === i ? 'active' : ''}`}
                >
                  <div className="erp-dashboard-opex-legend-color" style={{ backgroundColor: cat.color }} />
                  <span className="erp-dashboard-opex-legend-name">{cat.name}</span>
                  <span className="erp-dashboard-opex-legend-percentage">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/erp/financeiro/analise-fluxo-caixa')} 
            className="erp-dashboard-opex-btn"
          >
            Ver Análise Detalhada <ArrowRight size={14} />
          </button>
        </div>

        {/* Gráfico: Tempo em Reunião vs. Conversão */}
        <div className="erp-dashboard-orbit-card">
          <div className="erp-dashboard-orbit-header">
            <h3 className="erp-dashboard-orbit-title">Orbit: Reuniões vs. Conversão</h3>
            <Target size={16} className="erp-dashboard-orbit-icon" />
          </div>

          <div className="erp-dashboard-orbit-content">
            <div className="erp-dashboard-orbit-chart">
              {[
                { label: 'Sem 1', meetings: 40, conversion: 60 },
                { label: 'Sem 2', meetings: 70, conversion: 45 },
                { label: 'Sem 3', meetings: 55, conversion: 80 },
                { label: 'Sem 4', meetings: 90, conversion: 95 },
              ].map((d, i) => (
                <div key={i} className="erp-dashboard-orbit-bar-group">
                  <div className="erp-dashboard-orbit-bars">
                    <div 
                      className="erp-dashboard-orbit-bar erp-dashboard-orbit-bar-meetings" 
                      style={{ height: `${d.meetings}%` }}
                      title={`Tempo em Reunião: ${d.meetings}%`}
                    />
                    <div 
                      className="erp-dashboard-orbit-bar erp-dashboard-orbit-bar-conversion" 
                      style={{ height: `${d.conversion}%` }}
                      title={`Taxa de Conversão: ${d.conversion}%`}
                    />
                  </div>
                  <span className="erp-dashboard-orbit-bar-label">{d.label}</span>
                </div>
              ))}
            </div>

            <div className="erp-dashboard-orbit-legend">
              <div className="erp-dashboard-orbit-legend-item">
                <div className="erp-dashboard-orbit-legend-color erp-dashboard-orbit-legend-meetings" />
                <span>Tempo em Reunião</span>
              </div>
              <div className="erp-dashboard-orbit-legend-item">
                <div className="erp-dashboard-orbit-legend-color erp-dashboard-orbit-legend-conversion" />
                <span>Taxa de Conversão</span>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/erp/orbit')} 
            className="erp-dashboard-orbit-btn"
          >
            Ver Agenda Orbit <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Grid Inferior */}
      <div className="erp-dashboard-bottom-grid">
        {/* Operações Recentes */}
        <div className="erp-dashboard-recent-operations">
          <div className="erp-dashboard-recent-operations-header">
            <h3 className="erp-dashboard-recent-operations-title">Operações Recentes</h3>
            <button className="erp-dashboard-recent-operations-link">Ver Tudo</button>
          </div>
          <div className="erp-dashboard-recent-operations-list">
            {(dashboardData.recentOperations || []).slice(0, 4).map((op, i) => (
              <div key={i} className="erp-dashboard-recent-operations-item">
                <div className="erp-dashboard-recent-operations-icon">
                  <ArrowUpRight size={18} />
                </div>
                <div className="erp-dashboard-recent-operations-info">
                  <p className="erp-dashboard-recent-operations-number">Pedido #{op.number}</p>
                  <p className="erp-dashboard-recent-operations-customer">{op.customerName}</p>
                </div>
                <div className="erp-dashboard-recent-operations-amount">
                  {formatCurrencyBRL(op.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Widgets Inferiores */}
      <div className="erp-dashboard-widgets-grid">
        {/* Monitor de Ruptura */}
        <StockoutAlertWidget stockAlerts={dashboardData.stockAlerts} />

        {/* Esteira Inteligente de Compras */}
        <div className="erp-dashboard-purchase-pipeline">
          <div className="erp-dashboard-purchase-pipeline-header">
            <h3 className="erp-dashboard-purchase-pipeline-title">
              <BrainCircuit size={16} className="erp-dashboard-purchase-pipeline-icon" /> 
              Pipeline de Compras
            </h3>
            <span className="erp-dashboard-purchase-pipeline-status">Ativo</span>
          </div>

          <div className="erp-dashboard-purchase-pipeline-grid">
            {[
              { label: 'Revisão Técnica', count: dashboardData.purchasePipeline?.review || 0, icon: ListChecks, color: 'text-amber-500' },
              { label: 'Aguardando Mirror', count: dashboardData.purchasePipeline?.mirror || 0, icon: Zap, color: 'text-indigo-500' },
              { label: 'Aprovação Fin.', count: dashboardData.purchasePipeline?.finance || 0, icon: DollarSign, color: 'text-emerald-500' },
              { label: 'Follow-up', count: dashboardData.purchasePipeline?.followup || 0, icon: Clock, color: 'text-blue-500' },
            ].map((step, i) => (
              <div key={i} className="erp-dashboard-purchase-pipeline-item">
                <div className={`erp-dashboard-purchase-pipeline-item-icon ${step.color}`}>
                  <step.icon size={20} />
                </div>
                <p className="erp-dashboard-purchase-pipeline-item-count">{step.count}</p>
                <p className="erp-dashboard-purchase-pipeline-item-label">{step.label}</p>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => navigate('/erp/purchases')} 
            className="erp-dashboard-purchase-pipeline-btn"
          >
            Gerenciar Esteira
          </button>
        </div>
      </div>
    </div>
  )
}

export default ERPDashboardPage
